<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import Modal from "@/components/modals/ModalContainer.vue";
import { useRamp } from "@/composables/useRamp";
import { useMediaQuery } from "~/composables/useMediaQuery";
import { useTransak } from "@/composables/useTransak";
import { useWeb3AuthStore } from "~/stores/storeWeb3auth";
import { useClipboard } from "@vueuse/core";
import { useStoreUserV2 } from "~/stores/storeUserV2";
import { useStoreApp } from "~/stores/storeApp";
const storeUser = useStoreUserV2();
const storeApp = useStoreApp();

import { CircleCheck, Copy, QrCode as QrCodeIcon, X, ArrowRightLeft } from "lucide-vue-next";

import QRCode from "qrcode";

const web3AuthStore = useWeb3AuthStore();
const { copy } = useClipboard();

import LogoTransak from "/logo-transak.svg?url";
import LogoRamp from "/logo-ramp.svg?url";
import { parseWalletAddress } from "~/utils/wallets";

const copyTimeout = ref<{
  address: NodeJS.Timeout | undefined;
  pk: NodeJS.Timeout | undefined;
}>({ address: undefined, pk: undefined });

const isMobile = useMediaQuery("(max-width: 800px)");
const device = computed(() => (isMobile.value ? "mobile" : "desktop"));

const { showOverlay: showRampOverlay } = useRamp();
const { showOverlay: showTransakOverlay } = useTransak();

const props = defineProps<{
  show: boolean;
}>();

async function copyAddress() {
  copy(walletAddress.value);
  clearTimeout(copyTimeout.value.pk);
  copyTimeout.value.pk = setTimeout(() => {
    copyTimeout.value.pk = undefined;
  }, 2500);
}

const emit = defineEmits(["onClose"]);

const handleOnClose = () => {
  emit("onClose");
};

const selectRamp = () => {
  showRampOverlay({
    device: device.value,
    mode: "buy",
  });
  handleOnClose();
};

const selectTransak = () => {
  showTransakOverlay({
    device: device.value,
    mode: "buy",
  });
  handleOnClose();
};

const walletAddress = computed(() => {
  return web3AuthStore.walletAddress;
});

const walletLink = computed(() => {
  return `https://polygonscan.com/address/${walletAddress.value}`;
});

const walletLinkText = computed(() => {
  const resp = parseWalletAddress(walletAddress.value);
  return resp.text;
});

const canvasRef = ref<HTMLCanvasElement | null>(null);
const showQrCode = ref(false); // Pour gérer l'affichage du QR

const generateQrCode = async () => {
  showQrCode.value = true;
  await nextTick();
  const text = walletAddress.value;
  const logoSrc = "/images/pol-token.svg";

  const canvas = canvasRef.value;
  if (!canvas) return;

  const size = 300;

  // Augmenter la résolution pour un rendu plus net
  const scale = 2;
  canvas.width = size * scale;
  canvas.height = size * scale;
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;

  // Générer le QR code
  await QRCode.toCanvas(canvas, text, {
    width: size * scale,
    margin: 2,
    errorCorrectionLevel: "H",
  });

  // Ajouter le logo
  if (logoSrc) {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Améliorer la qualité du rendu
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    const logo = new Image();
    logo.crossOrigin = "anonymous";
    logo.src = logoSrc;

    await new Promise<void>((resolve) => {
      logo.onload = () => {
        const logoSize = size * scale * 0.25;
        const centerX = (size * scale) / 2;
        const centerY = (size * scale) / 2;
        const radius = logoSize / 2;
        const padding = 10 * scale;

        // Fond blanc circulaire
        ctx.fillStyle = "white";
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius + padding, 0, Math.PI * 2);
        ctx.fill();

        // Créer un masque circulaire pour le logo
        ctx.save();
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.clip();

        // Dessiner le logo (il sera coupé en cercle)
        ctx.drawImage(logo, centerX - radius, centerY - radius, logoSize, logoSize);

        ctx.restore();

        // Optionnel : ajouter une bordure circulaire
        ctx.strokeStyle = "#e0e0e0";
        ctx.lineWidth = 2 * scale;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.stroke();

        resolve();
      };
    });
  }
};
const hideQrCode = () => {
  showQrCode.value = false;
};

const showSwapModal = () => {
  storeUser.closeProfileDrawer();
  storeApp.showSwapModal = true;
  storeApp.showDepositModal = false;
};

const showWrapUnwrapOption = computed((): boolean => {
  // if (Number(web3AuthStore.wpolBalance) > 0 && web3AuthStore.connectionType === "auth") {
  //   return true;
  // }
  return true;
});
</script>

<template>
  <Modal :show="show" @onClose="handleOnClose" :title="$t('_token-transfer.deposit.title')">
    <div class="flex flex-col gap-4">
      <div>
        <div class="text-warning text-sm">
          {{ $t("_token-transfer.deposit.warning") }}
        </div>
      </div>
      <template v-if="!showQrCode">
        <p class="text-xs text-justify text-neutral-500">
          {{ $t("_token-transfer.deposit.description") }}
        </p>
        <div class="flex flex-col gap-4">
          <ButtonPrimary @click="selectRamp" class="text-left px-3">
            <img :src="LogoRamp" alt="Ramp" class="w-6 h-6 mr-2 inline-block" />
            Ramp
          </ButtonPrimary>
          <ButtonPrimary @click="selectTransak" class="text-left px-3">
            <img :src="LogoTransak" alt="Ramp" class="w-6 h-6 mr-2 inline-block" />
            Transak
          </ButtonPrimary>
          <ButtonPrimary v-if="showWrapUnwrapOption" @click="showSwapModal" class="text-left px-3">
            <div class="flex items-center gap-2">
              <div class="p-1 bg-zinc-800 rounded-full w-7 h-7 flex items-center justify-center">
                <ArrowRightLeft class="h-4 text-white" />
              </div>
              Swap
            </div>
          </ButtonPrimary>
        </div>

        <div class="h-[1px] w-full bg-zinc-900"></div>

        <div class="flex items-center p-2 py-0 rounded-lg mb-4">
          <div class="flex items-center gap-2 flex-grow">
            <img
              src="/images/pol-token.svg"
              alt="POL"
              class="w-8 h-8 inline-block mr-1 rounded-full"
            />
            <div>
              <div class="text-sm">{{ $t("_token-transfer.deposit.reception-wallet") }}</div>
              <div class="text-xs text-neutral-500">{{ walletLinkText }} (Polygon Network)</div>
            </div>
          </div>
          <div class="flex items-center gap-1">
            <CircleCheck v-if="copyTimeout.pk" class="h-5 text-green-60" />
            <div v-else class="p-1 hover:bg-zinc-900 rounded cursor-pointer">
              <Copy class="h-5" @click="copyAddress" />
            </div>
            <div @click="generateQrCode" class="p-1 hover:bg-zinc-900 rounded cursor-pointer">
              <QrCodeIcon class="h-5" />
            </div>
          </div>
        </div>
      </template>

      <div class="flex justify-center w-full" v-else>
        <div class="bg-zinc-900 rounded-lg flex flex-col justify-center w-full">
          <div class="w-full justify-end p-4 flex">
            <div
              @click="hideQrCode"
              class="flex p-1 justify-between hover:bg-zinc-800 cursor-pointer rounded"
            >
              <X />
            </div>
          </div>
          <div class="p-8 pt-0 flex justify-center">
            <canvas ref="canvasRef" class="rounded-lg max-w-56 max-h-56" />
          </div>
          <div class="w-full text-center">{{ walletLinkText }}</div>
          <div class="p-4 pt-0 text-xs text-center text-neutral-500">
            {{ $t("_token-transfer.deposit.scan-qr-code") }}
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>
